import {
  APIReportFilters,
  ArdoqId,
  DashboardFilterValues,
  PersistedMetamodel,
  ReportColumn,
  SlideTypes,
  ViewIds,
} from '@ardoq/api-types';
import { keepValidFilters, type ReportSort } from '@ardoq/report-reader';
import { isEmpty } from 'lodash';
import { AppModules } from 'appContainer/types';
import { isAddSlideFromCurrentViewModule } from 'appContainer/presentationEditorModules';
import { isOnPremise } from 'env';
import {
  MetamodelNavigationState,
  MetamodelPane,
} from 'metamodel/navigation/types';
import { isViewEligibleForNewAssets } from 'views/environmentals/restrictedViews';
import type { ModeViews } from 'views/modeViews$';
import {
  NewDashboardSlide,
  NewMetamodelSlide,
  NewReportSlide,
  NewSlide,
  NewVisualizationSlide,
} from './types';

export type DashboardSlideSource = {
  _id: ArdoqId;
  name: string;
  globalFilterValues?: DashboardFilterValues;
};

export type ReportSlideSource = {
  _id: ArdoqId;
  name: string;
  columns: ReportColumn[];
};

export type ReportSlideContext = {
  report: ReportSlideSource;
  sort: ReportSort | null;
  filters: APIReportFilters | null;
};

export type MetamodelSlideSource = {
  metamodelId: ArdoqId | null;
  name: string;
  fullOrg: boolean;
};

export const toDashboardSlide = (
  dashboard: DashboardSlideSource
): NewDashboardSlide => ({
  type: SlideTypes.DASHBOARD,
  dashboardId: dashboard._id,
  dashboardName: dashboard.name,
  dashboardParams: isEmpty(dashboard.globalFilterValues)
    ? null
    : { globalFilterValues: dashboard.globalFilterValues },
});

export const toReportSlide = ({
  report,
  sort,
  filters,
}: ReportSlideContext): NewReportSlide => ({
  type: SlideTypes.REPORT,
  reportId: report._id,
  reportName: report.name,
  reportParams: {
    sort: sort?.columnKey ?? undefined,
    order: sort?.order,
    filters: isEmpty(filters)
      ? null
      : keepValidFilters(filters, report.columns),
  },
});

export const toMetamodelSlide = ({
  metamodelId,
  name,
  fullOrg,
}: MetamodelSlideSource): NewMetamodelSlide => ({
  type: SlideTypes.METAMODEL,
  metamodelId,
  name,
  fullOrg,
});

export const toVisualizationSlide = (): NewVisualizationSlide => ({
  type: SlideTypes.VISUALIZATION,
});

export type NewSlideFromContextArgs = {
  selectedModule: AppModules;
  selectedDashboard: DashboardSlideSource | null;
  selectedReport: ReportSlideSource | null;
  selectedReportSort: ReportSort | null;
  selectedReportFilters: APIReportFilters | null;
  selectedMetamodel: PersistedMetamodel | null;
};

export const newSlideFromContext = ({
  selectedModule,
  selectedDashboard,
  selectedReport,
  selectedReportSort,
  selectedReportFilters,
  selectedMetamodel,
}: NewSlideFromContextArgs): NewSlide | null => {
  switch (selectedModule) {
    case AppModules.REPORTS:
      return selectedReport
        ? toReportSlide({
            report: selectedReport,
            sort: selectedReportSort,
            filters: selectedReportFilters,
          })
        : null;
    case AppModules.DASHBOARDS:
      return selectedDashboard ? toDashboardSlide(selectedDashboard) : null;
    case AppModules.METAMODEL:
      return selectedMetamodel
        ? toMetamodelSlide({
            metamodelId: selectedMetamodel._id,
            name: selectedMetamodel.name,
            fullOrg: selectedMetamodel.wholeOrg,
          })
        : null;
    default:
      return toVisualizationSlide();
  }
};

const isVisualizationSupported = (
  viewId: ViewIds,
  modeViewsById: ModeViews<ViewIds>['byIds'],
  hasCustomDomain: boolean
): boolean => {
  if (!isViewEligibleForNewAssets(modeViewsById[viewId])) {
    return false;
  }

  if (viewId !== ViewIds.NONE) {
    return true;
  }

  if (isOnPremise()) {
    return true;
  }

  return hasCustomDomain;
};

export type CanAddSlideArgs = {
  selectedModule: AppModules;
  selectedDashboard: DashboardSlideSource | null;
  selectedReport: ReportSlideSource | null;
  metamodelNavigation: MetamodelNavigationState;
  activeViewId: ViewIds;
  modeViewsById: ModeViews<ViewIds>['byIds'];
  hasCustomDomain: boolean;
  allowsAddToPresentation: boolean;
  isConvertedFromWorkspaceMode: boolean;
};

export const canAddSlide = ({
  selectedModule,
  selectedDashboard,
  selectedReport,
  metamodelNavigation,
  activeViewId,
  modeViewsById,
  hasCustomDomain,
  allowsAddToPresentation,
  isConvertedFromWorkspaceMode,
}: CanAddSlideArgs): boolean => {
  if (!allowsAddToPresentation) {
    return false;
  }

  if (!isAddSlideFromCurrentViewModule(selectedModule)) {
    return false;
  }

  const { pane, metamodelId } = metamodelNavigation;

  if (selectedModule === AppModules.REPORTS) {
    return Boolean(selectedReport);
  } else if (selectedModule === AppModules.DASHBOARDS) {
    return Boolean(selectedDashboard);
  } else if (selectedModule === AppModules.METAMODEL) {
    return Boolean(metamodelId) && pane === MetamodelPane.SELECTED;
  }
  return (
    Boolean(activeViewId) &&
    !isConvertedFromWorkspaceMode &&
    isVisualizationSupported(activeViewId, modeViewsById, hasCustomDomain)
  );
};
